import { supabase } from "../lib/supabase";
import { fetchMTDSpend } from "./spendService";
import { extractFunctionErrorMessage } from "./edgeFunctionError";

// Status can still change to "active" at/above this fraction of spend_cap, but campaign
// start-up (rollover_campaigns / change_campaign_status) is withheld until spend drops back down.
export const REACTIVATION_SPEND_CAP_THRESHOLD = 0.95;

// True once spend has consumed this fraction of the cap — the shared "effectively at cap"
// predicate used to gate manual reactivation.
export function isAtOrAboveCapThreshold(spendSum, cap) {
  return cap > 0 && spendSum >= REACTIVATION_SPEND_CAP_THRESHOLD * cap;
}

// Checks whether an advertiser has at least one active creative with an active variation.
// Returns { hasActiveCreative, error }.
export async function checkActiveCreative(advertiserId) {
  if (!supabase) return { hasActiveCreative: false, error: new Error("Supabase not configured") };
  const { data, error } = await supabase
    .from("creatives")
    .select("id, creative_variations!inner(id)")
    .eq("advertiser_id", advertiserId)
    .eq("status_id", 1)
    .eq("creative_variations.status_id", 1)
    .limit(1)
    .maybeSingle();
  return { hasActiveCreative: !!data, error };
}

// Pauses/resumes campaign delivery via the Portal API (through the portal-api Edge Function's
// campaigns resource), rather than the Cycle.io-backed change_campaign_status task. The Edge
// Function responds as soon as it knows which campaigns to change, then applies them in the
// background (EdgeRuntime.waitUntil) rather than blocking on every Portal API PATCH — so this
// resolves quickly, but a null `error` only means the batch was queued, not that every
// individual campaign PATCH has necessarily succeeded yet (per-campaign failures are logged
// server-side). Returns { error, queued }.
export async function setCampaignStatus({ advertiserId, status }) {
  if (!supabase) return { error: new Error("Supabase not configured") };
  const { data, error } = await supabase.functions.invoke("portal-api", {
    body: { resource: "campaigns", action: "set_status", advertiser_id: advertiserId, status },
  });
  if (error) return { error: new Error(await extractFunctionErrorMessage(error)) };
  return { error: null, queued: data?.queued ?? 0 };
}

// Dispatches the two jobs that actually start campaign delivery for an advertiser: rollover
// still runs as a Cycle.io backend job (no Portal API equivalent), campaign status now goes
// straight to the Portal API. Returns { error } — error is the first failure found, with both
// messages combined if both fail.
export async function dispatchActivationJobs({ advertiserId }) {
  if (!supabase) return { error: new Error("Supabase not configured") };
  const [{ error: rolloverError }, { error: statusError }] = await Promise.all([
    supabase.functions.invoke("trigger-backend-job", { body: { task_name: "rollover_campaigns", advertiser_id: advertiserId } }),
    setCampaignStatus({ advertiserId, status: true }),
  ]);
  if (!rolloverError && !statusError) return { error: null };
  // Extract both consistently — rolloverError is a raw supabase-js FunctionsHttpError (generic
  // "non-2xx status code" text unless unwrapped the same way); statusError is already an Error
  // with the extracted message from setCampaignStatus. Without this, a double-failure surfaces
  // a useless generic string next to a useful one instead of two useful ones.
  const [rolloverMessage, statusMessage] = await Promise.all([
    rolloverError ? extractFunctionErrorMessage(rolloverError) : null,
    statusError?.message ?? null,
  ]);
  const message = [rolloverMessage, statusMessage].filter(Boolean).join("; ");
  return { error: new Error(message || "Failed to trigger activation jobs") };
}

const RESUME_FAILED_MESSAGE = { message: "Couldn't automatically resume campaigns for this advertiser after the budget increase.", type: "warning" };
// Exported for callers that fetch previousCap themselves (BudgetDrawer, CampaignSettingsApprovalModal)
// and need the same toast when THAT lookup fails, before resumeCampaignsIfEligible is ever called.
export const RESUME_LOOKUP_FAILED_MESSAGE = { message: "Saved, but couldn't verify the previous approved spend cap — check manually whether campaigns need to be resumed.", type: "warning" };
const RESUME_SUCCESS_MESSAGE = { message: "Budget increase cleared the spend cap — resuming campaigns for this advertiser.", type: "success" };

// Resumes campaign delivery (not the advertiser account) when a cap increase clears the block
// the BE job placed via change_campaign_status(false) after MTD spend hit the cap. The advertiser
// itself never leaves "active" during that block — only delivery is paused — so this only ever
// dispatches change_campaign_status(true); there's nothing to roll over.
// Returns a { message, type } toast payload on a state-changing outcome, or null when nothing
// happened (not eligible, or eligibility couldn't be determined) — callers should stay silent on null.
export async function resumeCampaignsIfEligible({ advertiserId, previousCap, newCap }) {
  if (previousCap == null || newCap <= previousCap) return null;
  if (!supabase) return null;

  const { data: advertiser, error: advErr } = await supabase
    .from("advertisers")
    .select("status")
    .eq("id", advertiserId)
    .maybeSingle();
  if (advErr || advertiser?.status !== "active") return null;

  const { spendSum, error: spendErr } = await fetchMTDSpend(advertiserId);
  // Can't determine eligibility — stay silent rather than alarming over a transient issue.
  if (spendErr) return null;

  const wasBlockedByPreviousCap = isAtOrAboveCapThreshold(spendSum, previousCap);
  const hasHeadroomOnNewCap = !isAtOrAboveCapThreshold(spendSum, newCap);
  if (!wasBlockedByPreviousCap || !hasHeadroomOnNewCap) return null;

  const { error: jobErr } = await setCampaignStatus({ advertiserId, status: true });
  return jobErr ? RESUME_FAILED_MESSAGE : RESUME_SUCCESS_MESSAGE;
}
